/**
 * Pick the color a NEW product should land with already enabled.
 *
 * A product whose Sizes & Colors page opens with nothing enabled shows an
 * empty state and previews nothing until the distributor picks something.
 * The rule (Jake, 2026-09-01), in strict priority order:
 *
 *     blackish  ->  whiteish  ->  first color in the catalog
 *
 * The final fallback is what guarantees the page is never empty: even a
 * catalog of nothing but saturated mid-tones yields a pick.
 *
 * WHY THIS IS NOT `colorNameHeuristic.classifyByName`
 * ---------------------------------------------------
 * That helper answers a DIFFERENT question -- "should this garment be
 * decorated with light or dark thread" -- and its `DARK_WORDS` set is
 * correspondingly broad: it contains `red`, `blue`, `green`, `purple`,
 * `orange`, `pink`, `royal`, `crimson` and `teal`, because decoration treats
 * saturated mid-tones as dark. That grouping is right for decoration and
 * WRONG here: "Crimson" is not a blackish default. Its `LIGHT_WORDS` is
 * likewise broader than "whiteish" (`khaki`, `tan`, `sand`, `mint`, `lemon`,
 * `sky`).
 *
 * So this module defines a NARROWER blackish/whiteish notion beside it
 * rather than widening the shared one -- ColorFilmstrip, VariantMenu,
 * StageHeader and the ColorsPanel pips all depend on the existing
 * classification and must not shift because of this feature. `classifyByName`
 * is still used, but only as the hexless fallback, which is the role it was
 * built for.
 *
 * SIGNAL PRIORITY: hex first, name second. A supplier hex is a direct
 * measurement, and WCAG relative luminance handles "Deep Black" and "Carbon
 * Black" without enumerating them. The name heuristic is consulted only when
 * there is no usable hex -- which happens for real: a SanMar color row
 * carries `hex: null` for heathered / multi-color fabrics, where the swatch
 * image is the honest rendering.
 */
import { relativeLuminance } from '@/utils/luminance'
import { classifyByName } from '@/utils/colorNameHeuristic'

// Luminance thresholds, calibrated against the real SanMar fallback hexes in
// api/src/integration_resources/sanmar/color_hex_fallback.py:
//
//     Black       #000000  0.00000  |  White        #FFFFFF  1.00000
//     Deep Black  #0A0A0A  0.00304  |  Red          #C8102E  0.12847
//     Jet Black   #0B0B0B  0.00335  |  Athletic Red #B22234  0.10857
//     Navy        #1F2A44  0.02365  |  Royal        #05397B  0.04389
//     True Navy   #1A2B5F  0.02774  |  True Royal   #1E3F8C  0.05724
//
// BLACKISH_MAX sits at 0.030 -- above the navies (0.0237 / 0.0277, and the
// AdventHealth matrix explicitly lists "Night Navy" as a blackish default)
// and below the royals (0.0439+), so saturated blues do not qualify.
// WHITEISH_MIN at 0.60 admits white/ivory/cream while excluding mid-tones.
const BLACKISH_MAX = 0.030
const WHITEISH_MIN = 0.60

// A NAME that says black / white confirms a hex that is merely near the bar.
// Real SanMar "Black" is #343532 (luminance 0.0345) and S&S/SanMar "Black
// Heather" runs to 0.053 -- just over BLACKISH_MAX -- so hex alone called the
// literal black "not black" and the pick landed on a darker-hex maroon.
// These ceilings only apply when the name already says so; a contradicting
// hex (a "Black" row measuring white) is still believed over the name.
// Calibrated on the real catalog corpus (defaultColorPick.corpus.spec.js).
const NAME_CONFIRMED_BLACK_MAX = 0.10
const NAME_CONFIRMED_WHITE_MIN = 0.45

// Narrow name sets -- deliberately much smaller than colorNameHeuristic's.
// Only words that genuinely denote a near-black or a near-white.
//
// STRONG words name the color itself ("Black", "Jet Black", "White"); WEAK
// ones are dark/light by convention only ("Navy", "Charcoal", "Cream"). A
// strong word outranks a weak or hex-only match WITHIN a family, because a
// catalog row that SAYS "Black" is a better default than the first row whose
// hex merely happens to be dark (a maroon measures 0.019, under BLACKISH_MAX).
const STRONG_BLACK_WORDS = new Set(['black', 'jet', 'onyx', 'ebony'])
const WEAK_BLACK_WORDS = new Set(['navy', 'midnight', 'charcoal'])
const STRONG_WHITE_WORDS = new Set(['white'])
const WEAK_WHITE_WORDS = new Set(['ivory', 'cream', 'pearl', 'natural'])

// A base word followed by one of these names a DIFFERENT color or thing, not a
// near-black: "Black Cherry" is a red, "Jet Stream" a blue. (Whole-token
// matching already keeps "Blackberry" and "Whitewater" out.)
const NOT_A_BASE_FOLLOWERS = new Set([
    'cherry', 'berry', 'currant', 'plum', 'raspberry', 'stream', 'forest',
])

/**
 * ONE normalization step for every supplier's color naming: SanMar and S&S
 * both send the display name in `color_sku` ("Jet Black"), and some catalogs
 * and mainframe codes arrive compact ("JetBlack", "BlackHeather"). Spaces,
 * underscores, hyphens, dots and camelCase boundaries all become token
 * boundaries; a trailing `*` version marker is dropped.
 *
 * Two-tone names are slash-separated and the ORDER IS MEANINGFUL:
 * "Navy/White" and "White/Navy" are different colors on the same style
 * (AdventHealth matrix). The FIRST segment is the body color, so it decides.
 * This is exactly where the shared `classifyByName` cannot be reused -- it
 * splits on '/' and scans LIGHT before DARK, so it would classify BOTH of
 * those names as 'light'.
 *
 * @param {unknown} name
 * @returns {string[]} lowercase body-segment tokens
 */
export function colorWords(name) {
    if (typeof name !== 'string') return []
    const [first] = name.replace(/\*$/, '').split('/')
    return (first ?? '')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
        .toLowerCase()
        .split(/[^a-z0-9]+/)
        .filter(Boolean)
}

/** The row's display name: `name` when the payload has one, else `color_sku`. */
function displayName(color) {
    const n = color?.name
    return typeof n === 'string' && n.trim() !== '' ? n : color?.color_sku
}

/** Name evidence: { family, strong } or null. First base word in the name wins. */
function nameFamily(words) {
    for (let i = 0; i < words.length; i++) {
        const w = words[i]
        if (NOT_A_BASE_FOLLOWERS.has(words[i + 1])
            && (STRONG_BLACK_WORDS.has(w) || STRONG_WHITE_WORDS.has(w))) {
            continue
        }
        if (STRONG_BLACK_WORDS.has(w)) return { family: 'blackish', strong: true }
        if (WEAK_BLACK_WORDS.has(w)) return { family: 'blackish', strong: false }
        if (STRONG_WHITE_WORDS.has(w)) return { family: 'whiteish', strong: true }
        if (WEAK_WHITE_WORDS.has(w)) return { family: 'whiteish', strong: false }
    }
    return null
}

/**
 * Classify one catalog row: { family: 'blackish'|'whiteish', strong } | null.
 *
 * Hex wins when it parses (it is a measurement); the name is the fallback.
 * `strong` records whether the NAME also says so, and only ranks candidates
 * within a family. A hex that is a generic-name FALLBACK (`is_hex_fallback`)
 * is still usable: it was derived from the very name we would otherwise
 * inspect.
 */
function classifyRow(color) {
    const name = displayName(color)
    const words = colorWords(name)
    const byName = nameFamily(words)
    const lum = relativeLuminance(color?.hex)
    if (lum !== null) {
        if (byName?.strong) {
            if (byName.family === 'blackish' && lum <= NAME_CONFIRMED_BLACK_MAX) return byName
            if (byName.family === 'whiteish' && lum >= NAME_CONFIRMED_WHITE_MIN) return byName
        }
        let family = null
        if (lum <= BLACKISH_MAX) family = 'blackish'
        else if (lum >= WHITEISH_MIN) family = 'whiteish'
        if (!family) return null
        return { family, strong: byName?.family === family && byName.strong }
    }

    // No usable hex -- the name decides, reading only the body segment.
    if (byName) return byName
    // "Dark X" / "Deep X" with no recognized base word: defer to the shared
    // heuristic, but ONLY for its high-confidence verdict on the body
    // segment. Its low-confidence tier defaults mid-tones to 'dark', which
    // would wrongly promote a grey to blackish.
    const body = words.join(' ')
    const heuristic = classifyByName(body)
    if (heuristic.confidence === 'high' && heuristic.ld === 'dark'
        && (words.includes('dark') || words.includes('deep'))) {
        return { family: 'blackish', strong: false }
    }
    return null
}

/** 'blackish' | 'whiteish' | null for one catalog row (exported for the corpus tests). */
export function defaultColorFamily(color) {
    return classifyRow(color)?.family ?? null
}

/**
 * Choose the default color_sku from a supplier catalog.
 *
 * @param {Array<{color_sku?: string|null, name?: string, hex?: string|null}>} colors
 *   The supplier catalog rows, in catalog order.
 * @param {{ isRenderable?: (color: object) => boolean }} [opts]
 *   `isRenderable` is the editor's single renderability predicate (Artifi
 *   coverage). A row it rejects is never picked, at ANY rung. It must fail
 *   open (return true) for a color it cannot prove unrenderable.
 * @returns {string|null} The chosen `color_sku`, or null when the catalog
 *   holds no usable row OR every usable row is known unrenderable. A null
 *   result means "apply nothing" -- callers must NOT treat it as a reason to
 *   write an empty selection, and an unrenderable color is never selected on
 *   the distributor's behalf.
 */
export function pickDefaultColor(colors, opts = {}) {
    if (!Array.isArray(colors)) return null
    const { isRenderable } = opts ?? {}

    // Only rows with a usable sku are candidates: the sku IS the stored
    // value, so a blank one would persist an unselectable entry.
    const usable = colors.filter(
        (c) => typeof c?.color_sku === 'string' && c.color_sku.trim() !== ''
            && (typeof isRenderable !== 'function' || isRenderable(c) !== false),
    )
    if (usable.length === 0) return null

    // Single pass. Family priority beats everything; within a family a
    // name-evident row (strong) beats a hex-only one; catalog order breaks
    // the remaining ties.
    const best = { blackish: [null, null], whiteish: [null, null] } // [strong, weak]
    for (const c of usable) {
        const cls = classifyRow(c)
        if (!cls) continue
        const slot = best[cls.family]
        const i = cls.strong ? 0 : 1
        if (slot[i] === null) slot[i] = c.color_sku
    }
    return best.blackish[0] ?? best.blackish[1]
        ?? best.whiteish[0] ?? best.whiteish[1]
        ?? usable[0].color_sku
}
